/**
 * PATH: apps/qa/src/pages/ncr-detail/NCRActivityPanel.tsx
 * PURPOSE: Activity timeline and add-action panel for NCR detail view
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ncrApi } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { MessageSquare, Plus, Loader2 } from 'lucide-react'
import type { NCRAction } from './helpers'

export function NCRActivityPanel({ ncrId, actions, status }: {
  ncrId: string
  actions: NCRAction[]
  status: string
}) {
  const queryClient = useQueryClient()
  const [actionNotes, setActionNotes] = useState('')

  const addActionMutation = useMutation({
    mutationFn: async () => ncrApi.addComment(ncrId, { action_text: actionNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-ncr', ncrId] })
      setActionNotes('')
    },
  })

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border p-6">
      <h2 className="text-lg font-semibold text-foreground mb-4">Activity</h2>

      {actions.length ? (
        <div className="space-y-4">
          {actions.map((action, idx) => (
            <div key={action.id || idx} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-foreground">
                    {action.actor_name || action.created_by_name || 'Unknown'}
                  </span>
                  <span className="text-muted-foreground">added a {action.action_type}</span>
                  {action.created_at && (
                    <span className="text-muted-foreground">• {formatDateTime(action.created_at)}</span>
                  )}
                </div>
                {(action.action_text || action.notes) && (
                  <p className="text-foreground mt-1">{action.action_text || action.notes}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground">No activity yet.</p>
      )}

      {status !== 'closed' && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex gap-3">
            <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)}
              placeholder="Add a note or update..." rows={2}
              className="flex-1 px-4 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none" />
            <button onClick={() => addActionMutation.mutate()} disabled={addActionMutation.isPending || !actionNotes.trim()}
              className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 self-end">
              {addActionMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
